import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useWidgetTokenStore } from '@elfsight/lib-utils';
import { useSettings } from '@wu/widget-core';
import {
  fetchGooglePhoto,
  GOOGLE_FETCH_STATUS,
  getFormattedManualImages
} from '../../helpers';

const ImagesContext = createContext({ isLoading: true, images: [] });

export function ImagesProvider({ children }) {
  const {
    photosProvider,
    googlePhotosIntegration,
    images: manualImages
  } = useSettings();
  const widgetTokenStore = useWidgetTokenStore();

  const [isLoading, setIsLoading] = useState(true);
  const [images, setImages] = useState([]);
  const abortControllerRef = useRef(null);
  const { source, album } = googlePhotosIntegration || {};
  const googleMediaCount = parseInt(album?.mediaItemsCount || 0);

  const hasGoogleIntegration = photosProvider === 'google';

  const contextRef = useRef({ album, googleMediaCount });

  const handleLoadGooglePhotos = useCallback(async () => {
    if (!source) {
      if (contextRef.current.album) {
        setIsLoading(true);
      }

      return;
    }

    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    const { status, images: googlePhotoImages } = await fetchGooglePhoto({
      source,
      signal: abortControllerRef.current.signal,
      widgetToken: widgetTokenStore.get()
    });

    if (status === GOOGLE_FETCH_STATUS.ABORT) {
      return;
    }

    setImages(googlePhotoImages);
    setIsLoading(false);
  }, [source]);

  useEffect(() => {
    contextRef.current = { album, googleMediaCount };
  }, [album, googleMediaCount]);

  useEffect(() => {
    if (!hasGoogleIntegration) {
      const formattedManualImages = getFormattedManualImages(manualImages);
      setImages(formattedManualImages);
      setIsLoading(false);

      return;
    }

    const isEmptyGoogleAlbum =
      hasGoogleIntegration && !contextRef.current.googleMediaCount;
    if (isEmptyGoogleAlbum) {
      setIsLoading(false);
      setImages([]);
    } else {
      handleLoadGooglePhotos();
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [handleLoadGooglePhotos, hasGoogleIntegration, manualImages]);

  const value = useMemo(
    () => ({
      isLoading,
      images,
      hasGoogleIntegration
    }),
    [isLoading, images, hasGoogleIntegration]
  );

  return (
    <ImagesContext.Provider value={value}>{children}</ImagesContext.Provider>
  );
}

export function useImages() {
  return useContext(ImagesContext);
}
